import React, { useState, useEffect } from 'react';
import './ServiceModal.css';
import VastuService from './VastuService';

const ServiceModal = ({ service, location, onLocationSelect, onClose, currentLanguage }) => {
  const [showLocationSelect, setShowLocationSelect] = useState(!location);
  const [showServiceDetails, setShowServiceDetails] = useState(false);

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleLocationClick = (selectedLocation) => {
    onLocationSelect(selectedLocation);
    setShowLocationSelect(false);
    setShowServiceDetails(true);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderServiceCategories = () => {
    if (!service.categories) return null;

    return (
      <div className="service-categories">
        <h3>Available Services</h3>
        <div className="categories-grid">
          {Object.values(service.categories).map((category) => (
            <div key={category.id} className="category-card">
              <div className="category-icon">
                <i className={category.icon}></i>
              </div>
              <h4>{category.title}</h4>
              <p>{category.description}</p>
              
              {/* Regular pricing */}
              {category.pricing && (
                <div className="pricing-info">
                  <div className="price-tag">
                    {category.pricing.startingFrom && (
                      <span className="starting-from">Starting from</span>
                    )}
                    <span className="currency">{location === 'india' ? '₹' : '$'}</span>
                    <span className="amount">
                      {location === 'india' ? category.pricing.india?.toLocaleString() : category.pricing.abroad?.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Special pricing for Vastu Visit */}
              {category.pricingOptions && (
                <div className="pricing-options">
                  <div className="pricing-option">
                    <h5>{category.pricingOptions.withPackage.title}</h5>
                    <div className="price-tag">
                      <span className="currency">{location === 'india' ? '₹' : '$'}</span>
                      <span className="amount">
                        {location === 'india' ? 
                          category.pricingOptions.withPackage.price.india?.toLocaleString() : 
                          category.pricingOptions.withPackage.price.abroad?.toLocaleString()
                        }
                      </span>
                    </div>
                    <p>{category.pricingOptions.withPackage.description}</p>
                  </div>
                  
                  <div className="pricing-option">
                    <h5>{category.pricingOptions.withoutPackage.title}</h5>
                    <div className="individual-services">
                      {Object.values(category.pricingOptions.withoutPackage.services).map((service, index) => (
                        <div key={index} className="individual-service">
                          <span className="service-name">{service.title}</span>
                          <div className="price-tag small">
                            <span className="currency">{location === 'india' ? '₹' : '$'}</span>
                            <span className="amount">
                              {location === 'india' ? 
                                service.price.india?.toLocaleString() : 
                                service.price.abroad?.toLocaleString()
                              }
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {category.subServices && (
                <div className="sub-services">
                  <small>Includes: {Object.values(category.subServices).map(sub => sub.title).join(', ')}</small>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!service) return null;

  return (
    <div className="service-modal-overlay" onClick={handleBackdropClick}>
      <div className="service-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>

        {showLocationSelect && (
          <div className="location-selection">
            <div className="modal-header">
              <div className="service-modal-icon">
                <i className={service.icon}></i>
              </div>
              <h2>{service.title}</h2>
              <p>{service.description}</p>
            </div>
            
            <div className="location-cards">
              <div 
                className="location-card india-card"
                onClick={() => handleLocationClick('india')}
              >
                <div className="location-flag">
                  <i className="fas fa-flag"></i>
                </div>
                <h3>India</h3>
                <p>Domestic consultation services</p>
                <div className="location-features">
                  <span><i className="fas fa-check"></i> In-person visits available</span>
                  <span><i className="fas fa-check"></i> Local language support</span>
                  <span><i className="fas fa-check"></i> INR pricing</span>
                </div>
              </div>
              
              <div 
                className="location-card abroad-card"
                onClick={() => handleLocationClick('abroad')}
              >
                <div className="location-flag">
                  <i className="fas fa-globe"></i>
                </div>
                <h3>International</h3>
                <p>Global consultation services</p>
                <div className="location-features">
                  <span><i className="fas fa-check"></i> Online consultations</span>
                  <span><i className="fas fa-check"></i> Multiple time zones</span>
                  <span><i className="fas fa-check"></i> USD pricing</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {!showLocationSelect && showServiceDetails && (
          <div className="service-details">
            <div className="modal-header">
              <button className="back-btn" onClick={() => setShowLocationSelect(true)}>
                <i className="fas fa-arrow-left"></i>
              </button>
              <div className="service-modal-icon">
                <i className={service.icon}></i>
              </div>
              <h2>{service.title}</h2>
              <p>{service.description}</p>
              <div className="location-badge">
                <i className={location === 'india' ? 'fas fa-flag' : 'fas fa-globe'}></i>
                {location === 'india' ? 'India' : 'International'}
              </div>
            </div>
            
            {renderServiceCategories()}
            
            <div className="contact-section">
              <h3>Ready to Book?</h3>
              <p>Contact us for personalized consultation and booking</p>
              <div className="pricing-note">
                <i className="fas fa-info-circle"></i>
                <span>All prices are indicative and may vary based on specific requirements. Final pricing will be confirmed during consultation.</span>
              </div>
              <div className="contact-buttons">
                <a href="tel:+918160569755" className="contact-btn primary">
                  <i className="fas fa-phone"></i>
                  Call Now
                </a>
                <a href={`https://wa.me/918160569755?text=Hello%20Ankit%20Raval,%0A%0AI%20am%20interested%20in%20${encodeURIComponent(service.title)}%20consultation%20for%20${location === 'india' ? 'India' : 'International'}%20location.%0A%0APlease%20provide%20me%20with%20more%20details%20about%20the%20services%20and%20booking%20process.%0A%0AThank%20you!`} className="contact-btn whatsapp" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-whatsapp"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceModal;